import { faEnvelope } from "@fortawesome/pro-regular-svg-icons/faEnvelope";
import { faIdCard } from "@fortawesome/pro-regular-svg-icons/faIdCard";
import { faMapMarkerAlt } from "@fortawesome/pro-regular-svg-icons/faMapMarkerAlt";
import { faUserFriends } from "@fortawesome/pro-regular-svg-icons/faUserFriends";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Divider, Grid, Menu, MenuItem, Typography } from '@mui/material';
import { Alert } from "@mui/lab";
import * as coreApi from "api/Core";
import { AppImages } from 'assets/appResources';
import ButtonEllipsis from 'components/Buttons/ButtonEllipsis';
import ButtonFollow from 'components/Buttons/ButtonFollow';
import ButtonNewQuestion from 'components/Buttons/ButtonNewQuestion';
import IconButtonClose from 'components/Buttons/IconButtonClose';
import IconButtonNotify from 'components/Buttons/IconButtonNotify';
import IconButtonSilience from 'components/Buttons/IconButtonSilience';
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import IconBlocked from "components/Icons/IconBlocked";
import MuiRegularButton from 'components/MuiExtras/Button/MuiRegularButton';
import { GridEx } from 'components/MuiExtras/Grid/GridEx';
import MuiConfirmation from 'components/MuiExtras/MuiConfirmation';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiImage from 'components/MuiExtras/MuiImage';
import MuiLink from "components/MuiExtras/MuiLink";
import MuiPaper from 'components/MuiExtras/MuiPaper';
import MuiPushRight from 'components/MuiExtras/MuiPushRight';
import MuiColor from 'components/MuiExtras/Typography/MuiColor';
import MuiDanger from "components/MuiExtras/Typography/MuiDanger";
import MuiWarning from "components/MuiExtras/Typography/MuiWarning";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { getFirstName } from "components/UsernameInspect";
import HtmlUtils from 'lib/HtmlUtils';
import LibUtils from 'lib/LibUtils';
import { SystemRoles } from 'models/ApiEnums';
import { GetUserProfileDto, UserProfileRelationDto } from 'models/ApiModels';
import * as React from 'react';
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import { RoutePaths } from 'routes/RouteConfig';
import Session from 'services/Session';
import CreateQuestion from 'views/Publication/CreateQuestion';
import UserPhones from 'views/User/Components/UserPhones';

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    menuAnchorEl: any;
    dialog: any;
    editUser: boolean;
    allBadges: boolean;
    lastError: any;
    followUpdating: boolean;
}

interface IProps {
    profile: GetUserProfileDto;
    showOverflowMenu?: boolean;
    showFullProfileButton?: boolean;

    onRelationChange?: (follow: UserProfileRelationDto) => void;
    onQuestionCreated?: () => void;
    onInfoRequest?: () => void;
    onEditProfile?: () => void;
    onFollowers?: () => void;
    onClose?: () => void;
}

type Props = IProps & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

class UserProfileCardHeader extends React.PureComponent<Props, IState> {
    private mailtoRef: any;

    ///
    constructor(props: Props) {
        super(props);
        this.state = {
            allBadges: false,
            dialog: null,
            editUser: false,
            menuAnchorEl: null,
            lastError: null,
            followUpdating: false
        };
    }

    ///
    private res(r: string, f?: any) {
        return this.props.t("Views.User.Components.UserProfileCardHeader." + r, f);
    }

    ///
    private handleOpenMenu(e: React.MouseEvent<HTMLElement>) {
        this.setState({ menuAnchorEl: e.currentTarget });
    }

    ///
    private handleCloseMenu() {
        this.setState({ menuAnchorEl: null });
    }

    ///
    private handleFollow = () => {
        const { profile: { userId, name, currentUserRelation: { following } }, onRelationChange } = this.props;

        this.setState({ followUpdating: true });

        coreApi.followUser(userId, !following)
            .then((result) => {
                if (result) {

                    onRelationChange(result);

                    if (!following)
                        this.props.snackbar.showMessage(this.res("FollowingMessage", { name: LibUtils.firstWord(name) }), "success");
                    else
                        this.props.snackbar.showMessage(this.res("UnfollowMessage", { name: LibUtils.firstWord(name) }), "warning");
                };
            })
            .finally(() => {
                this.setState({ followUpdating: false });
            });
    };

    ///
    private handleNewQuestion = () => {
        const { profile: { userId, name }, onQuestionCreated } = this.props;

        this.setState({
            dialog: (
                <CreateQuestion
                    requestAnswerFromUser={userId}
                    onClose={
                        (newOrUpdatePublicationId) => {
                            if (newOrUpdatePublicationId) {
                                this.props.snackbar.showMessage(this.res("QuestionRepassMessage", { name }), "success");
                                if (onQuestionCreated) onQuestionCreated();
                            }
                            this.setState({ dialog: null });
                        }
                    }
                />
            )
        });
    };

    ///
    private handleEditRecord = () => {
        this.handleCloseMenu();
        this.props.onEditProfile();
    };

    ///
    private handleShowGroups = () => {
        this.handleCloseMenu();
    };

    ///
    private handleMailTo = () => {
        this.handleCloseMenu();
        this.mailtoRef.click();
    };

    ///
    private handleNotify = () => {
        const { profile: { userId, name, currentUserRelation }, onRelationChange } = this.props;

        const firstName = LibUtils.firstWord(name);

        coreApi.setFollowedUserNotification(userId, !currentUserRelation.notifyChanges)
            .then((newRelation) => {

                onRelationChange(newRelation);

                if (newRelation.notifyChanges)
                    this.props.snackbar.showMessage(this.res("ReceiveNotificationsMessage", { name: firstName }), "success");
                else
                    this.props.snackbar.showMessage(this.res("NotReceiveNotificationsMessage", { name: firstName }), "warning");

                this.setState({ dialog: null });
            })
            .catch(() => {
                this.setState({ dialog: null });
            });
    };

    ///
    private handleSilience = () => {
        const { profile: { userId, name, currentUserRelation }, onRelationChange } = this.props;

        this.setState({
            menuAnchorEl: null,
            dialog: (
                <MuiConfirmation
                    message={
                        <Trans
                            i18nKey={
                                currentUserRelation.muted
                                    ? "Views.User.Components.UserProfileCardHeader.ConfirmationUnmuteMessage"
                                    : "Views.User.Components.UserProfileCardHeader.ConfirmationMuteMessage"
                            }
                            values={{ name }}
                        />
                    }
                    onClose={
                        (confirmed) => {
                            if (confirmed) {

                                (
                                    currentUserRelation.muted
                                        ? coreApi.userUnmuteAuthor(userId)
                                        : coreApi.userMuteAuthor(userId)
                                )
                                    .then((result) => {
                                        onRelationChange(result);

                                        if (currentUserRelation.muted)
                                            this.props.snackbar.showMessage(this.res("UnmutedMessage"), "success");
                                        else
                                            this.props.snackbar.showMessage(this.res("MutedMessage"), "warning");

                                        this.setState({ dialog: null });
                                    })
                                    .catch((error) => {
                                        this.setState({ dialog: null, lastError: error });
                                    });
                            }
                            else {
                                this.setState({ dialog: null });
                            }
                        }
                    }
                />
            )
        });
    };

    ///
    private handleBlockUser = () => {
        const { profile: { userId, name, currentUserRelation }, onRelationChange } = this.props;

        this.setState({
            menuAnchorEl: null,
            dialog: (
                <MuiConfirmation
                    message={
                        <Trans
                            i18nKey={
                                currentUserRelation.blocked
                                    ? "Views.User.Components.UserProfileCardHeader.ConfirmationUnblockMessage"
                                    : "Views.User.Components.UserProfileCardHeader.ConfirmationBlockMessage"
                            }
                            values={{ name }}
                        />
                    }
                    onClose={
                        (confirmed) => {
                            if (confirmed) {

                                (
                                    currentUserRelation.blocked
                                        ? coreApi.userUnblockAnotherUser(userId)
                                        : coreApi.userBlockAnotherUser(userId)
                                )
                                    .then((result) => {

                                        onRelationChange(result);

                                        if (currentUserRelation.blocked)
                                            this.props.snackbar.showMessage(this.res("UnblockedMessage"), "success");
                                        else
                                            this.props.snackbar.showMessage(this.res("BlockedMessage"), "warning");

                                        this.setState({ dialog: null });
                                    })
                                    .catch((error) => {
                                        this.setState({ dialog: null, lastError: error });
                                    });
                            }
                            else {
                                this.setState({ dialog: null });
                            }
                        }
                    }
                />
            )
        });
    };

    ///
    private getMenuOverflow() {

        var result: Array<React.ReactElement> = [];
        if (this.props.profile.userId == Session.user.id) return result;

        const { currentUserRelation, deleted, blocked } = this.props.profile;

        //if (Session.hasRole(SystemRoles.UserManager)) {
        //    result.push(<MenuItem onClick={this.handleEditRecord}><span>Editar Cadastro</span></MenuItem>)
        //    result.push(<MenuItem onClick={this.handleShowGroups}><span>Grupos onde é Membro</span></MenuItem>)
        //}

        if (!currentUserRelation.muted && !currentUserRelation.blocked && !currentUserRelation.blockedByProfile) {
            result.push(
                <MenuItem key="silience" onClick={this.handleSilience}>
                    <span>{this.props.t("Views.User.Common.Actions.Mute")}</span>
                </MenuItem>
            );
        }

        if (!currentUserRelation.blocked) {
            result.push(
                <MenuItem key="block" onClick={this.handleBlockUser}>
                    <span>
                        {
                            this.props.profile.currentUserRelation.blocked
                                ? this.props.t("Views.User.Common.Actions.UnblockUser")
                                : this.props.t("Views.User.Common.Actions.BlockUser")
                        }
                    </span>
                </MenuItem>
            );
        }

        if (!currentUserRelation.blockedByProfile && !deleted && !blocked) {
            if (result.length > 0) result.push(<Divider key="div2" />);
            result.push(
                <MenuItem key="mailTo" onClick={this.handleMailTo}>
                    <span>{this.props.t("Views.User.Common.Actions.MailTo")}</span>
                </MenuItem>
            );
        }

        if (Session.hasRole(SystemRoles.UserManager | SystemRoles.Administrator) && this.props.onEditProfile) {
            if (result.length > 0) result.push(<Divider key="div3" />);
            result.push(
                <MenuItem key="editRecord" onClick={this.handleEditRecord}>
                    <span>{this.props.t("Views.User.Common.Actions.EditAccount")}</span>
                </MenuItem>
            );
        }

        if (this.props.onInfoRequest) {
            if (result.length > 0) result.push(<Divider key="div4" />);
            result.push(
                <MenuItem key="info" onClick={() => { this.handleCloseMenu(); this.props.onInfoRequest(); }}>
                    <span>{this.props.t("Views.User.Common.Actions.MoreInfo")}</span>
                </MenuItem>
            );
        }

        return result;
    }

    ///
    private buildButtons() {
        const { profile, onRelationChange } = this.props;

        if (profile.userId == Session.user.id)
            return null;

        return (
            <>
                {
                    onRelationChange &&
                    <GridEx item>
                        <Grid container wrap="nowrap" justifyContent="flex-start" alignItems="center" spacing={1}>
                            {
                                profile.currentUserRelation.blocked === false && !profile.currentUserRelation.blockedByProfile && !profile.deleted &&
                                <Grid item>
                                    <MuiColor variant="mutedDark">
                                        <ButtonFollow
                                            following={profile.currentUserRelation.following}
                                            onClick={this.handleFollow}
                                            updating={this.state.followUpdating}
                                        />
                                    </MuiColor>
                                </Grid>
                            }

                            {
                                profile.currentUserRelation.following && profile.currentUserRelation.muted === false && profile.currentUserRelation.blocked === false && !profile.currentUserRelation.blockedByProfile && !profile.deleted &&
                                <Grid item>
                                    <IconButtonNotify
                                        color={profile.currentUserRelation.notifyChanges ? "success" : "muted"}
                                        active={profile.currentUserRelation.notifyChanges}
                                        titleActive={this.res("ButtonNotifyActiveTitle")}
                                        titleInactive={this.res("ButtonNotifyInactiveTitle")}
                                        onClick={this.handleNotify}
                                    />
                                </Grid>
                            }

                            {
                                profile.currentUserRelation.muted === true && profile.currentUserRelation.blocked === false && !profile.currentUserRelation.blockedByProfile &&
                                <Grid item>
                                    <IconButtonSilience
                                        active={profile.currentUserRelation.muted}
                                        titleActive={this.res("ButtonMuteActiveTitle")}
                                        titleInactive={this.res("ButtonMuteInactiveTitle")}
                                        onClick={this.handleSilience}
                                    />
                                </Grid>
                            }

                            {
                                profile.currentUserRelation.blocked === true &&
                                <>
                                    <Grid item>
                                        <MuiRegularButton
                                            color="warning"
                                            onClick={this.handleBlockUser}
                                        >
                                            <IconBlocked fixedWidth />
                                            <span>{this.props.t("Views.User.Common.Actions.UnblockUser")}</span>
                                        </MuiRegularButton>
                                    </Grid>
                                    <Grid item>
                                        <MuiWarning>{this.res("CurrentlyBlocked")}</MuiWarning>
                                    </Grid>
                                </>
                            }
                        </Grid>
                    </GridEx>
                }

                {
                    profile.currentUserRelation.muted === false && profile.currentUserRelation.blocked === false && !profile.currentUserRelation.blockedByProfile && !profile.blocked && !profile.deleted &&
                    <GridEx item>
                        <MuiColor variant="mutedDark">
                            <ButtonNewQuestion
                                onClick={this.handleNewQuestion}
                            />
                        </MuiColor>
                    </GridEx>
                }
            </>
        );
    }

    ///
    private buildFullProfileButton() {
        if (!this.props.showFullProfileButton) return null;

        return (
            <GridEx item>
                <MuiColor variant="mutedDark">
                    <MuiRegularButton
                        color="transparent"
                        onClick={(e) => { HtmlUtils.openUrlInNewWindow(RoutePaths.UserProfileViewer.replace(":id?", this.props.profile.userId)); }}
                    >
                        <FontAwesomeIcon icon={faIdCard} fixedWidth />
                        &nbsp;
                        <span>{this.props.t("Views.User.Common.Actions.FullProfile")}</span>
                    </MuiRegularButton>
                </MuiColor>
            </GridEx>
        );
    }

    ///
    private buildOverflowMenu() {
        if (!this.props.showOverflowMenu) return null;

        const { menuAnchorEl } = this.state;
        const items = this.getMenuOverflow();

        if (items.length == 0) return null;

        return (
            <GridEx item xs>
                <MuiPushRight>
                    <ButtonEllipsis
                        onClick={(e) => { this.handleOpenMenu(e); }}
                    />
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={menuAnchorEl != null}
                        onClose={() => { this.handleCloseMenu(); }}
                    >
                        {items}
                    </Menu>
                </MuiPushRight>
            </GridEx>
        );
    }

    ///
    public render() {
        const { profile } = this.props;
        const { lastError } = this.state;

        return (
            <>
                {this.state.dialog}

                <RequestErrorDialog
                    open={lastError != null}
                    error={lastError}
                    onClose={() => this.setState({ lastError: null })}
                />

                {
                    <MuiPaper>

                        <MuiContainer margin="dense">

                            <GridEx container wrap="nowrap" spacing={2} >

                                <GridEx item>
                                    <MuiImage
                                        src={profile && profile.thumbnail ? profile.thumbnail.url : AppImages.DefaultUserThumbnail}
                                        rounded={true}
                                        orientation="square"
                                    />
                                </GridEx>

                                <GridEx item xs>
                                    <MuiContainer margin="dense">

                                        <GridEx container justifyContent="space-between">
                                            <GridEx item xs>
                                                <Typography variant="h6">
                                                    <span>{profile.name}</span>
                                                </Typography>
                                            </GridEx>
                                            {
                                                this.props.onClose &&
                                                <GridEx item>
                                                    <IconButtonClose size="small" onClick={this.props.onClose} />
                                                </GridEx>
                                            }
                                        </GridEx>

                                        {
                                            profile.currentUserRelation.blockedByProfile &&
                                            <Grid item>
                                                <Alert severity="warning"><span>{this.res("BlockedByLiteral")} <b>{getFirstName(profile.name)}</b></span></Alert>
                                            </Grid>
                                        }

                                        <Typography variant="body1" component="div">
                                            <MuiColor variant="muted">
                                                <small>
                                                    {
                                                        !profile.deleted && !profile.blocked && !profile.currentUserRelation.blockedByProfile &&
                                                        <div><FontAwesomeIcon icon={faEnvelope} fixedWidth />&nbsp;<a href={"mailto:" + profile.email} style={{ textDecoration: "none" }}><MuiColor variant="muted">{profile.email}</MuiColor></a></div>
                                                    }
                                                    {
                                                        profile.locationName &&
                                                        <div><FontAwesomeIcon icon={faMapMarkerAlt} fixedWidth />&nbsp;{profile.locationName}</div>
                                                    }
                                                </small>
                                            </MuiColor>
                                        </Typography>


                                        {
                                            !profile.currentUserRelation.blockedByProfile && profile.followersCount > 0 &&
                                            <Typography variant="body1" component="div">
                                                <MuiLink onClick={() => this.props.onFollowers()} underline="none" >
                                                    <MuiColor variant="muted">
                                                        <small>
                                                            <FontAwesomeIcon icon={faUserFriends} fixedWidth />&nbsp;<span>{this.res("Followers", { count: profile.followersCount })}</span>
                                                        </small>
                                                    </MuiColor>
                                                </MuiLink>
                                            </Typography>
                                        }
                                    </MuiContainer>

                                    {
                                        !profile.deleted && !profile.blocked && !profile.currentUserRelation.blockedByProfile && profile.personal.phones &&
                                        <MuiContainer margin="dense">
                                            <Typography variant="body2" component="div">
                                                <UserPhones phones={profile.personal.phones} />
                                            </Typography>
                                        </MuiContainer>
                                    }

                                    {
                                        profile.deleted &&
                                        <MuiDanger showIcon>{this.res("IsDeletedMessage")}</MuiDanger>
                                    }
                                    {
                                        profile.blocked &&
                                        <MuiWarning showIcon>{this.res("IsBlockedMessage")}</MuiWarning>
                                    }
                                </GridEx>
                            </GridEx>
                        </MuiContainer>

                        <GridEx container justifyContent="flex-start" alignItems="center">
                            {this.buildButtons()}
                            {this.buildFullProfileButton()}
                            {this.buildOverflowMenu()}
                        </GridEx>

                        <a ref={(ref) => this.mailtoRef = ref}
                            style={{ display: "none" }}
                            href={`mailto:${profile.email}`}
                        />
                    </MuiPaper>
                }
            </>
        );
    }
}

export default withSnackbar(withTranslation()(UserProfileCardHeader));